import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { fmtDate } from "../lib/templates";
import { INTRO_VARIANTS } from "../components/IntroPicker";
import { toast } from "sonner";

function DemoSeederModal({ open, onClose }) {
  const [slug, setSlug] = useState("aarav-diya-091403");
  const [templateId, setTemplateId] = useState("royal_indian");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!slug.trim()) return toast.error("Slug is required");
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/demo/seed", {
        slug: slug.trim(),
        template_id: templateId || undefined,
        title: title.trim() || undefined,
      });
      setResult(r.data);
      toast.success(`Demo ${r.data.action === "created" ? "created" : "updated"} — /i/${r.data.slug} is now live`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create demo");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="demo-seeder-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full p-7 md:p-9 border border-stone-200 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-stone-400 text-2xl leading-none hover:text-stone-900" aria-label="Close">×</button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-700 mb-2">Admin · One-click Demo</div>
        <h3 className="font-serif-display text-2xl md:text-3xl">Create a public Demo Invitation</h3>
        <p className="text-sm text-stone-500 mt-2">Spins up a paid, demo-flagged invitation at <code className="bg-stone-100 px-1.5 py-0.5">/i/&lt;slug&gt;</code> — perfect for ad landing pages. If the slug already exists, it just marks it paid + demo (idempotent).</p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Public slug</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="demo-slug-input" placeholder="aarav-diya-091403" className="w-full mt-1 px-3 py-2.5 border border-stone-300 focus:border-stone-900 outline-none text-sm font-mono" />
            <div className="text-[10px] text-stone-400 mt-1">Will be live at <code>{`/i/${slug || "..."}`}</code></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Template</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="demo-template-select" className="w-full mt-1 px-3 py-2.5 border border-stone-300 outline-none text-sm bg-white">
                <option value="royal_indian">Royal Indian</option>
                <option value="floral_elegance">Floral Elegance</option>
                <option value="minimalist_modern">Minimalist Modern</option>
                <option value="bengali_lotus">Bengali Lotus</option>
                <option value="punjabi_phulkari">Punjabi Phulkari</option>
                <option value="christian_chapel">Christian Chapel</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Title (optional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ad Landing" className="w-full mt-1 px-3 py-2.5 border border-stone-300 outline-none text-sm" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="demo-seed-submit" className="w-full py-3 bg-emerald-600 text-white text-xs uppercase tracking-[0.3em] font-semibold disabled:opacity-60 hover:bg-emerald-500">
            {busy ? "Creating…" : "Create Demo Invitation →"}
          </button>
        </form>

        {result && (
          <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200" data-testid="demo-seed-result">
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-800 font-semibold mb-2">✓ {result.action}</div>
            <a href={`/i/${result.slug}`} target="_blank" rel="noreferrer" className="block text-emerald-900 font-mono text-sm underline break-all">/i/{result.slug}</a>
            <div className="text-xs text-emerald-700 mt-1">Template: {result.template_id}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Sparkline({ data, color = "#1A1918", label }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 100;
  const h = 40;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const pts = data.map((d, i) => `${i * step},${h - (d.count / max) * h}`).join(" ");
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{label}</div>
        <div className="text-xs text-stone-400">14-day trend</div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-12">
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        {data.map((d, i) => (
          <circle key={i} cx={i * step} cy={h - (d.count / max) * h} r="1.2" fill={color} />
        ))}
      </svg>
      <div className="flex justify-between text-[9px] text-stone-400 mt-1">
        <span>{data[0]?.date?.slice(5) || ""}</span>
        <span>{data[data.length - 1]?.date?.slice(5) || "today"}</span>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [invs, setInvs] = useState([]);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/users").then((r) => setUsers(r.data)),
      api.get("/admin/invitations").then((r) => setInvs(r.data)),
    ]).then(() => setRefreshedAt(new Date()));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // live refresh every 30s
    return () => clearInterval(t);
  }, []);

  const kpis = stats
    ? [
        { l: "Sign ups", v: stats.users, sub: "Total accounts", color: "#1A1918" },
        { l: "Websites", v: stats.invitations, sub: "Invitations created", color: "#047857" },
        { l: "Page views", v: stats.total_views ?? 0, sub: "All public visits", color: "#0E7490" },
        { l: "RSVPs", v: stats.rsvps, sub: `${stats.attending?.yes || 0} attending`, color: "#4338CA" },
      ]
    : [];

  const maxTmpl = Math.max(1, ...(stats?.top_templates?.map((t) => t.count) || [1]));

  // Intro variant popularity (computed client-side from the enriched invitations list)
  const introCounts = invs.reduce((acc, i) => {
    const v = i.intro_variant || "envelope";
    acc[v] = (acc[v] || 0) + 1;
    return acc;
  }, {});
  const introRows = Object.entries(introCounts)
    .map(([id, count]) => ({ id, count, label: INTRO_VARIANTS.find((v) => v.id === id)?.label || id }))
    .sort((a, b) => b.count - a.count);
  const maxIntro = Math.max(1, ...introRows.map((r) => r.count));
  const introLabel = (id) => INTRO_VARIANTS.find((v) => v.id === id)?.label || id;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-page">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-3">Studio · Admin</div>
            <h1 className="font-serif-display text-5xl md:text-6xl font-light tracking-tight">Who&apos;s using Weddinsite</h1>
            <p className="text-stone-500 mt-2 text-sm">Live numbers across every account on the platform.{refreshedAt && ` · Refreshed ${refreshedAt.toLocaleTimeString()}`}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setDemoModalOpen(true)} data-testid="admin-create-demo-btn" className="px-4 py-2 bg-stone-900 text-amber-300 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-stone-800">⚡ Create Demo Invitation</button>
            <button
              onClick={async () => {
                if (!confirm("Seed/refresh ALL 23 template demos? Existing demo invitations will be updated.")) return;
                try {
                  const r = await api.post("/admin/demo/seed-all-templates");
                  toast.success(`Done — ${r.data.created} created, ${r.data.updated} updated`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Seed failed");
                }
              }}
              data-testid="admin-seed-all-demos-btn"
              className="px-4 py-2 bg-amber-300 text-stone-900 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-amber-200"
            >
              ★ Seed all 23 demos
            </button>
            <Link to="/admin/orders" data-testid="admin-orders-link" className="px-4 py-2 bg-emerald-600 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-500">₹ Orders & Revenue</Link>
            <Link to="/admin/leads" data-testid="admin-leads-link" className="px-4 py-2 bg-amber-500 text-stone-900 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-amber-400">★ Custom Leads ₹8K</Link>
            <Link to="/admin/testimonials" data-testid="admin-testimonials-link" className="px-4 py-2 bg-rose-500 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-rose-400">♥ Testimonials</Link>
            <button onClick={load} data-testid="admin-refresh" className="px-4 py-2 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100">Refresh</button>
          </div>
        </div>

        <DemoSeederModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} />

        {/* KPI CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-kpis">
          {kpis.map((s, i) => (
            <div key={i} className="bg-white border border-stone-200 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{s.l}</div>
              <div className="font-serif-display text-5xl mt-2" style={{ color: s.color }}>{s.v ?? "—"}</div>
              <div className="text-xs text-stone-500 mt-2">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* TRENDS */}
        <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="admin-trends">
          <div className="bg-white border border-stone-200 p-6">
            <Sparkline data={stats?.daily_signups || Array(14).fill({ date: "", count: 0 })} color="#1A1918" label="New sign ups" />
          </div>
          <div className="bg-white border border-stone-200 p-6">
            <Sparkline data={stats?.daily_invitations || Array(14).fill({ date: "", count: 0 })} color="#047857" label="New websites created" />
          </div>
        </div>

        {/* TOP TEMPLATES */}
        {stats?.top_templates?.length > 0 && (
          <div className="bg-white border border-stone-200 mb-6" data-testid="admin-top-templates">
            <div className="px-6 py-4 border-b border-stone-200 font-serif-display text-2xl">Most loved templates</div>
            <div className="p-6 space-y-3">
              {stats.top_templates.map((t) => (
                <div key={t.template_id} className="flex items-center gap-4">
                  <div className="w-48 text-sm font-medium uppercase tracking-[0.1em]">{t.template_id.replace(/_/g, " ")}</div>
                  <div className="flex-1 h-2 bg-stone-100 overflow-hidden">
                    <div className="h-full bg-stone-900" style={{ width: `${(t.count / maxTmpl) * 100}%` }} />
                  </div>
                  <div className="w-10 text-right font-serif-display text-lg">{t.count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INTRO ANIMATION POPULARITY */}
        {introRows.length > 0 && (
          <div className="bg-white border border-stone-200 mb-10" data-testid="admin-intro-popularity">
            <div className="px-6 py-4 border-b border-stone-200 flex items-baseline justify-between">
              <span className="font-serif-display text-2xl">Opening animation popularity</span>
              <span className="text-xs text-stone-500">Across {invs.length} websites</span>
            </div>
            <div className="p-6 space-y-3">
              {introRows.map((r) => (
                <div key={r.id} className="flex items-center gap-4" data-testid={`intro-row-${r.id}`}>
                  <div className="w-32 text-sm font-medium">{r.label}</div>
                  <div className="flex-1 h-2 bg-stone-100 overflow-hidden">
                    <div className="h-full bg-indigo-600" style={{ width: `${(r.count / maxIntro) * 100}%` }} />
                  </div>
                  <div className="w-10 text-right font-serif-display text-lg">{r.count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RECENT LISTS */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-stone-200" data-testid="admin-users-list">
            <div className="px-6 py-4 border-b border-stone-200 flex items-baseline justify-between">
              <span className="font-serif-display text-2xl">Recent sign ups</span>
              <span className="text-xs text-stone-500">{users.length} total</span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {users.length === 0 ? (
                <div className="px-6 py-8 text-sm text-stone-500 text-center">No sign ups yet.</div>
              ) : users.map((u) => (
                <div key={u.id} className="px-6 py-3 border-b border-stone-100 flex justify-between text-sm last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{u.name}</div>
                    <div className="text-stone-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">{u.role}</div>
                    <div className="text-xs text-stone-400">{fmtDate(u.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-stone-200" data-testid="admin-invs-list">
            <div className="px-6 py-4 border-b border-stone-200 flex items-baseline justify-between">
              <span className="font-serif-display text-2xl">Recent websites</span>
              <span className="text-xs text-stone-500">{invs.length} total</span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {invs.length === 0 ? (
                <div className="px-6 py-8 text-sm text-stone-500 text-center">No websites yet.</div>
              ) : invs.map((i) => (
                <div key={i.id} className="px-6 py-3 border-b border-stone-100 text-sm last:border-b-0" data-testid={`admin-inv-row-${i.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{i.title}</div>
                      <div className="text-stone-600 text-xs truncate">by <span className="font-medium text-stone-800">{i.owner_name}</span> · {i.owner_email}</div>
                      <div className="text-stone-400 text-[10px] mt-0.5 font-mono-disp">/i/{i.slug} · {i.template_id.replace(/_/g, " ")}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-stone-400">{fmtDate(i.created_at)}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
                    <span className="text-stone-600"><span className="font-semibold text-stone-900">{i.views_count || 0}</span> views</span>
                    <span className="text-stone-600"><span className="font-semibold text-stone-900">{i.rsvp_count || 0}</span> rsvps</span>
                    <span className="text-indigo-700 uppercase tracking-[0.15em] text-[10px] bg-indigo-50 px-1.5 py-0.5" title="Opening animation">Intro: {introLabel(i.intro_variant)}</span>
                    <a href={`/i/${i.slug}`} target="_blank" rel="noreferrer" className="ml-auto text-emerald-700 hover:underline uppercase tracking-[0.15em] text-[10px]">Open ↗</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
